import React, { useState } from 'react';
import { Search, Mail, MapPin, Building, User, Globe, AlertCircle, X, Send, Paperclip, Copy, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';
import EmailPopup from './emailpop/EmailPopup';

// Toast Component
const Toast = ({ message, type, isVisible, onClose }) => {
  if (!isVisible) return null;

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500 text-white border-green-600';
      case 'error':
        return 'bg-red-500 text-white border-red-600';
      case 'warning':
        return 'bg-yellow-500 text-white border-yellow-600';
      default:
        return 'bg-blue-500 text-white border-blue-600';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border-l-4 min-w-96 ${getToastStyles()}`}>
        <span className="text-lg">{getIcon()}</span>
        <div className="flex-1">
          <p className="font-medium">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const ContactFinder = () => {
  const [url, setUrl] = useState('');
  const [contacts, setContacts] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [socialMedia, setSocialMedia] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [emailPopup, setEmailPopup] = useState({ isOpen: false, contact: null });
  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: '',
    subject: '',
    message: '',
    selectedTemplate: 0,
    attachments: []
  });
  const [isEmailSending, setIsEmailSending] = useState(false);

  // Toast state
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'info'
  });

  // Toast functions
  const showNotification = (message, type = 'info') => {
    setToast({
      isVisible: true,
      message,
      type
    });

    // Auto-hide after 5 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, isVisible: false }));
    }, 5000);
  };

  const hideNotification = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const departments = ['All', 'Executive', 'Marketing', 'Sales', 'HR', 'Support', 'Management', 'Communication'];

  // Email templates for affiliate outreach
  const emailTemplates = [
    {
      subject: "Partnership Opportunity - Website Affiliate Program",
      message: `Hello [Name],

I hope this email finds you well. My name is [Your Name], and I'm reaching out from [Your Website/Company Name].

I've been following your work and really admire what you've built with [Their Website]. Your content quality and audience engagement are impressive.

I'm writing to explore a potential affiliate partnership opportunity. We're looking to collaborate with quality websites like yours to create mutually beneficial relationships.

Could you please share:
• Your current affiliate program criteria and requirements
• Commission structure and payment terms
• Any specific content or promotional guidelines
• Minimum traffic or performance thresholds

I'd be happy to provide more details about our program and discuss how we can work together effectively.

Looking forward to hearing from you.

Best regards,
[Your Name]
[Your Title]
[Your Contact Information]`
    },
    {
      subject: "Collaboration Inquiry - Affiliate Partnership",
      message: `Hi [Name],

I hope you're having a great day! I'm [Your Name] from [Your Website/Company Name].

I came across [Their Website] and was impressed by your content and community. Your approach to [specific niche/topic] really resonates with what we're trying to achieve.

We're expanding our affiliate program and would love to explore a partnership with you. Our program offers competitive commissions and excellent support for our partners.

I'd appreciate if you could share:
• Your affiliate partnership requirements
• What commission rates you typically work with
• Your audience demographics and traffic insights
• Any specific promotional preferences or restrictions

We believe there's great potential for a successful collaboration and would love to discuss this further at your convenience.

Thank you for your time, and I look forward to your response.

Warm regards,
[Your Name]
[Your Position]
[Contact Details]`
    },
    {
      subject: "Affiliate Program Inquiry - [Your Website Name]",
      message: `Dear [Name],

Greetings from [Your Website/Company Name]! I'm [Your Name], and I handle our partnership development.

I've been researching quality websites in our niche and [Their Website] consistently comes up as a leader in the space. Your content quality and audience engagement metrics are exactly what we look for in potential partners.

We're currently accepting applications for our affiliate program and believe you'd be an excellent fit. Our program features:
• Competitive commission structures
• Dedicated affiliate support
• Marketing materials and resources
• Timely payments and transparent reporting

To move forward, I'd love to learn more about:
• Your current affiliate partnerships and criteria
• Preferred commission models (flat rate, percentage, tiered)
• Content integration preferences
• Any questions about our products/services

Would you be open to a brief call or email exchange to discuss this opportunity in more detail?

Best wishes,
[Your Name]
[Your Role]
[Your Website/Company Name]
[Contact Information]`
    }
  ];

  const openEmailPopup = (contact) => {
    setEmailPopup({ isOpen: true, contact });
    setEmailForm({
      to: contact.value,
      cc: '',
      subject: emailTemplates[0].subject,
      message: emailTemplates[0].message,
      selectedTemplate: 0,
      attachments: []
    });
  };

  const closeEmailPopup = () => {
    setEmailPopup({ isOpen: false, contact: null });
    setEmailForm({
      to: '',
      cc: '',
      subject: '',
      message: '',
      selectedTemplate: 0,
      attachments: []
    });
  };

  const selectTemplate = (templateIndex) => {
    setEmailForm(prev => ({
      ...prev,
      selectedTemplate: templateIndex,
      subject: emailTemplates[templateIndex].subject,
      message: emailTemplates[templateIndex].message
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setEmailForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const removeAttachment = (index) => {
    setEmailForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  // Enhanced email sending function with proper API integration
  const sendEmail = async () => {
    try {
      setIsEmailSending(true);
      
      // Validate required fields
      if (!emailForm.to || !emailForm.subject || !emailForm.message) {
        showNotification('Please fill in all required fields (To, Subject, Message)', 'error');
        return;
      }
      
      // Prepare payload
      const payload = {
        to: emailForm.to.trim(),
        cc: emailForm.cc ? emailForm.cc.trim() : '',
        subject: emailForm.subject.trim(),
        message: emailForm.message.trim(),
        attachments: emailForm.attachments || [],
        sender_email_id: emailForm.sender_email_id || 'primary'
      };


      // Send email via backend API
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      const response = await fetch(`${apiUrl}/api/emailsent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      // Get response text first to handle both JSON and non-JSON responses
      const responseText = await response.text();
      let result;
      
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', responseText);
        throw new Error(`Server returned invalid JSON: ${responseText}`);
      }


      if (!response.ok) {
        throw new Error(result.error || result.message || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        showNotification('Email sent successfully!', 'success');
        closeEmailPopup();
      } else {
        throw new Error(result.error || result.message || 'Failed to send email');
      }
      
    } catch (error) {
      console.error('Error sending email:', error);
      
      // Show more specific error messages
      let errorMessage = 'Failed to send email';
      if (error.message.includes('verify your email')) {
        errorMessage = 'Please verify your sender email in settings first';
      } else if (error.message.includes('required fields')) {
        errorMessage = 'Please fill in all required fields';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setIsEmailSending(false);
    }
  };

  // Get available departments (only show tabs that have contacts)
  const getAvailableDepartments = () => {
    const departmentCounts = {};
    
    // Count contacts per department
    contacts.forEach(contact => {
      if (contact.department) {
        departmentCounts[contact.department] = (departmentCounts[contact.department] || 0) + 1;
      }
    });
    
    // Start with 'All' if there are any contacts
    const availableDepartments = contacts.length > 0 ? ['All'] : [];
    
    // Add departments that have contacts
    departments.slice(1).forEach(dept => {
      if (departmentCounts[dept] > 0) {
        availableDepartments.push(dept);
      }
    });
    
    return { availableDepartments, departmentCounts };
  };
  
  const { availableDepartments, departmentCounts } = getAvailableDepartments();

  // Extract and normalize domain from URL
  const extractDomain = (input) => {
    try {
      // Remove protocol if present
      let domain = input.replace(/^https?:\/\//, '');
      // Remove www. if present
      domain = domain.replace(/^www\./, '');
      // Remove path, query params, and fragments
      domain = domain.split('/')[0].split('?')[0].split('#')[0];
      // Remove port if present
      domain = domain.split(':')[0];
      
      return domain.toLowerCase().trim();
    } catch (e) {
      return input.toLowerCase().trim();
    }
  };

  // Validate domain format
  const isValidDomain = (domain) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;
    return domainRegex.test(domain);
  };

  const handleSubmit = async () => {
    // Clear previous results
    setError('');
    setContacts([]);
    setCompanyInfo(null);
    setSocialMedia({});

    // Validate input
    if (!url.trim()) {
      setError('Please enter a domain or website URL');
      showNotification('Please enter a domain or website URL', 'warning');
      return;
    }

    const domain = extractDomain(url);
    
    // Validate domain format
    if (!isValidDomain(domain)) {
      setError('Please enter a valid domain (e.g., example.com)');
      showNotification('Please enter a valid domain (e.g., example.com)', 'error');
      return;
    }

    setLoading(true);

    try {
      // Using the environment variable for API URL
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact-finder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ domain }),
      });

      // Get response data first
      let data;
      try {
        const responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error('Invalid response from server');
      }

      // Handle different response statuses
      if (!response.ok) {
        // Check if it's a limit exceeded error (403 status)
        if (response.status === 403 && data.error === 'CONTACT_FINDER_LIMIT_EXCEEDED') {
          // Use the detailed message from the backend
          const limitMessage = data.message || `Contact finder limit exceeded for your ${data.data?.userPlan || 'current'} plan. Please upgrade to continue.`;
          throw new Error(limitMessage);
        }
        // Handle other errors
        throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
      }

      // Handle successful response
      if (data.error) {
        throw new Error(data.error);
      }

      // Set company info and contacts from API response
      if (data.company) {
        setCompanyInfo(data.company);
      }

      // Set social media if available
      if (data.socialMedia) {
        setSocialMedia(data.socialMedia);
      }

      // Count social media links
      const socialCount = data.socialMedia ? Object.values(data.socialMedia).flat().filter(Boolean).length : 0;

      if (data.contacts && Array.isArray(data.contacts)) {
        setContacts(data.contacts);
        // Reset to 'All' tab when new results come in
        setActiveTab('All');

        // Show success message with usage info if available
        let successMessage = `Found ${data.contacts.length} contacts`;
        if (socialCount > 0) {
          successMessage += ` and ${socialCount} social profiles`;
        }
        successMessage += ` for ${domain}`;
        if (data.usageInfo) {
          successMessage += `. Usage: ${data.usageInfo.currentCount}/${data.usageInfo.allowedCount === -1 ? 'Unlimited' : data.usageInfo.allowedCount}`;
        }
        showNotification(successMessage, 'success');
      } else if (socialCount > 0) {
        setContacts([]);
        showNotification(`Found ${socialCount} social profiles for ${domain}`, 'success');
      } else {
        setContacts([]);
        showNotification('No contacts found for this domain', 'info');
      }

    } catch (err) {
      console.error('API Error:', err);
      const errorMessage = err.message || 'Failed to fetch contact information. Please try again.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    if (activeTab === 'All') return true;
    return contact.department && contact.department === activeTab;
  });

  const getDepartmentColor = (department) => {
    const colors = {
      'Executive': 'bg-purple-100 text-purple-800',
      'Marketing': 'bg-blue-100 text-blue-800',
      'Sales': 'bg-green-100 text-green-800',
      'HR': 'bg-orange-100 text-orange-800',
      'Support': 'bg-cyan-100 text-cyan-800',
      'Management': 'bg-indigo-100 text-indigo-800',
      'Communication': 'bg-pink-100 text-pink-800'
    };
    return colors[department] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    return status === 'valid' ? 'text-green-600' : 'text-gray-500';
  };

  // Copy to clipboard function with toast notification
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification('Email content copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy:', err);
      showNotification('Failed to copy to clipboard', 'error');
    }
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-lg">
                  <Search className="w-6 h-6 text-white" />
                </div>
                Contact Finder
              </h1>
              <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                Find professional email addresses for any domain
              </p>
              <p className="text-sm text-blue-500 dark:text-blue-400 mt-2">Enter a domain like: example.com, company.com, or https://website.com</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 shadow-sm">
                <span className="text-sm font-medium text-blue-600">Contact Discovery Suite</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Form */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-6">
            <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Domain Contact Search</h2>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            {/* Domain Input */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Enter Domain or Website URL
              </label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="e.g., example.com or https://example.com"
                className="w-full px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:border-blue-500 dark:focus:border-blue-600 transition-all duration-200 hover:shadow-md h-12 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100"
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              />
            </div>

            {/* Search Button */}
            <div className="lg:w-48 w-full">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 disabled:transform-none disabled:shadow-none h-12"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                <span>{loading ? 'Searching...' : 'Find Contacts'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6">
          <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-xl p-4 flex items-center gap-2">
            <AlertCircle className="text-red-500 dark:text-red-400" size={20} />
            <span className="text-red-700 dark:text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Company Info */}
      {companyInfo && (
        <div className="mb-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-4">Company Information</h2>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Building className="text-blue-500" size={16} />
                <span className="text-gray-700">{companyInfo.organization || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="text-blue-500" size={16} />
                <span className="text-gray-700">{companyInfo.domain}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="text-blue-500" size={16} />
                <span className="text-gray-700">{contacts.length} contacts found</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="text-blue-500" size={16} />
                <span className="text-gray-700">{companyInfo.city}, {companyInfo.country}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building className="text-blue-500" size={16} />
                <span className="text-gray-700">{companyInfo.industry}</span>
              </div>
            </div>
            {companyInfo.description && (
              <p className="text-gray-600 mt-4">{companyInfo.description}</p>
            )}
          </div>
        </div>
      )}

      {/* Social Media Section */}
      {Object.values(socialMedia).some(arr => arr && arr.length > 0) && (
        <div className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Social Media Profiles</h2>
            <div className="flex flex-wrap gap-4">
              {/* LinkedIn */}
              {socialMedia.linkedin && socialMedia.linkedin.length > 0 && (
                socialMedia.linkedin.map((url, idx) => (
                  <a
                    key={`linkedin-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                  >
                    <Linkedin className="text-blue-700" size={20} />
                    <span className="text-blue-700 font-medium">LinkedIn</span>
                  </a>
                ))
              )}

              {/* Twitter */}
              {socialMedia.twitter && socialMedia.twitter.length > 0 && (
                socialMedia.twitter.map((url, idx) => (
                  <a
                    key={`twitter-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-sky-50 hover:bg-sky-100 rounded-lg transition-colors border border-sky-200"
                  >
                    <Twitter className="text-sky-500" size={20} />
                    <span className="text-sky-600 font-medium">Twitter</span>
                  </a>
                ))
              )}

              {/* Facebook */}
              {socialMedia.facebook && socialMedia.facebook.length > 0 && (
                socialMedia.facebook.map((url, idx) => (
                  <a
                    key={`facebook-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200"
                  >
                    <Facebook className="text-indigo-600" size={20} />
                    <span className="text-indigo-600 font-medium">Facebook</span>
                  </a>
                ))
              )}

              {/* Instagram */}
              {socialMedia.instagram && socialMedia.instagram.length > 0 && (
                socialMedia.instagram.map((url, idx) => (
                  <a
                    key={`instagram-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-pink-50 hover:bg-pink-100 rounded-lg transition-colors border border-pink-200"
                  >
                    <Instagram className="text-pink-600" size={20} />
                    <span className="text-pink-600 font-medium">Instagram</span>
                  </a>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Department Tabs - Only show if there are available departments */}
      {availableDepartments.length > 0 && (
        <div className="mb-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-2">
            <div className="flex flex-wrap gap-2">
              {availableDepartments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setActiveTab(dept)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 ${
                    activeTab === dept
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-600/30'
                      : 'text-blue-600 hover:bg-blue-50 hover:shadow-md'
                  }`}
                >
                  {dept} {dept !== 'All' && departmentCounts[dept] && `(${departmentCounts[dept]})`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contact Cards */}
      {filteredContacts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContacts.map((contact, index) => (
            <div key={index} className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] overflow-hidden">
              {/* Header */}
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-slate-100 text-lg">
                      {contact.first_name && contact.last_name
                        ? `${contact.first_name} ${contact.last_name}`
                        : contact.value?.split('@')[0] || 'N/A'
                      }
                    </h3>
                    <p className={`text-sm font-medium ${getStatusColor(contact.verification?.status)}`}>
                      {contact.verification?.status === 'valid' ? 'Verified' : 'Unverified'}
                    </p>
                  </div>
                  {contact.department && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDepartmentColor(contact.department)}`}>
                      {contact.department}
                    </span>
                  )}
                </div>

                {/* Contact Details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail className="text-blue-500" size={16} />
                    <span className="text-gray-700 text-sm">{contact.value}</span>
                  </div>
                  
                  {contact.position && (
                    <div className="flex items-center gap-2">
                      <User className="text-blue-500" size={16} />
                      <span className="text-gray-700 text-sm">{contact.position}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <MapPin className="text-blue-500" size={16} />
                    <span className="text-gray-700 text-sm">
                      {companyInfo?.city || 'Not Provided'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Globe className="text-blue-500" size={16} />
                    <span className="text-gray-700 text-sm">{companyInfo?.domain}</span>
                  </div>

                  {contact.confidence && (
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Confidence:</span>
                      <span className={`font-medium ${contact.confidence > 85 ? 'text-green-600' : contact.confidence > 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {contact.confidence}%
                      </span>
                    </div>
                  )}
                </div>

                {/* Contact Button */}
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openEmailPopup(contact);
                  }}
                  className="w-full mt-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-2 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40"
                >
                  <Mail className="w-4 h-4" />
                  Contact
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results - only show if no contacts AND no social media */}
      {!loading && contacts.length === 0 && companyInfo && !Object.values(socialMedia).some(arr => arr && arr.length > 0) && (
        <div className="text-center">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            <Search className="mx-auto text-blue-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
            <p className="text-gray-600">No email contacts or social profiles were found for this domain.</p>
          </div>
        </div>
      )}

      {/* Email Popup */}
      <EmailPopup
        isOpen={emailPopup.isOpen}
        onClose={closeEmailPopup}
        recipient={emailPopup.contact}
        onSend={async (emailFormData, recipient) => {
          const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
          const response = await fetch(`${apiUrl}/api/emailsent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ to: emailFormData.to.trim(), cc: emailFormData.cc ? emailFormData.cc.trim() : '', subject: emailFormData.subject.trim(), message: emailFormData.message.trim(), attachments: emailFormData.attachments || [], sender_email_id: emailFormData.sender_email_id || 'primary' })
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.error || 'Failed to send email');
          if (result.success) showNotification('Email sent successfully!', 'success');
          else throw new Error(result.error || 'Failed to send email');
        }}
        templates={emailTemplates}
        customTitle={emailPopup.contact ? `Contact ${emailPopup.contact.first_name || ''} ${emailPopup.contact.last_name || ''}`.trim() : ''}
        readOnlyTo={true}
      />

      {/* Toast Component */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
};

export default ContactFinder;